/**
 * The in-app statement of what CricketCompass measures about how it is used
 * — issue #224's "a written statement of what is collected, in-app and in
 * the repo", the in-app half. The repo half is `docs/instrumentation.md`,
 * which arrives with the instrumentation itself (#364).
 *
 * ANDREAS'S RULING, 2026-08-21: **disclosure, not consent.** People are told
 * that usage is measured; collection is not gated behind an opt-in, and
 * there is no opt-in prompt, banner or modal. #224 left that open because
 * the users include minors and #169's guardian model decides who would be
 * consenting. The ruling still holds.
 *
 * D218 (#1106, #1109) REPLACES THREE STATEMENTS THIS FILE USED TO MAKE:
 * that the browser sends no usage data, that there is no per-user
 * dimension, and that there is no switch. Each became false when the per-account
 * record landed (#1107, #1108): the app now sends events, each carries a
 * random per-account code, and a player or coach can switch the counting off
 * (D220 Q1a 90 days then totals, Q2a players and coaches only, Q8a the
 * shipped switch row; D229 an admin account is never counted).
 *
 * THIS SHIPS BEFORE THE COUNTING DOES, ON PURPOSE. The ordering was the one
 * thing that could not be recovered later: an app that starts measuring and
 * says so afterwards has already done the thing it is disclosing. So the
 * statement lands first and describes a standing property of the product.
 *
 * ONE COPY, TWO DOORS. There are exactly two ways to end up inside this app,
 * and each one carries the statement:
 *
 *   - `/register` — a new account. On the new look `USAGE_DISCLOSURE_ONE_LINE`
 *     with a link to the privacy notice (D446, #1626); on the old look
 *     `USAGE_DISCLOSURE_SHORT`. Immediately above the button that creates it.
 *     For an under-11 the adult filling that form in is the guardian (#169),
 *     which is the whole reason this placement is the primary one.
 *   - `/settings` — everyone already signed in, which at pilot scale is
 *     everyone the first counts will actually be about. The full statement,
 *     via `UsageDisclosureCard`.
 *
 * D205 (Andreas, 2026-10-01, #1023): this used to be THREE doors — the
 * landing page's demo buttons were the one door that skipped the sign-up
 * form, and carried the short sentence too. There is no public demo any
 * more, so there is no third door to carry it.
 *
 * Both read from here, so they can never drift into promising different
 * things — the failure mode CLAUDE.md's labels section is about, applied to
 * a promise instead of a label list.
 *
 * EVERY LINE HERE MUST STAY TRUE OF `api/src/lib/usage.ts` AND OF THE
 * `usage_events` TABLE (`api/src/db/schema.ts`). The per-account record is
 * closed vocabularies (event name, screen, tab, item, how it was reached,
 * role, device class), the time, and a random code per account. If a column
 * is added to it, this file is part of that change, not a follow-up to it;
 * `api/test/usage.test.ts` fails when a column has no field below.
 *
 * WHY IT DOES NOT SAY "ANONYMOUS". `docs/instrumentation.md` states plainly
 * that aggregate is not the same as anonymous at our size: role is a
 * dimension, and the coach population is small enough that a row narrows to
 * a person. The per-account code makes that stronger, not weaker. The paragraph about that is the reason this text is
 * worth reading rather than the reason to soften it, and
 * `usageDisclosure.test.js` fails if the word "anonymous" ever appears here
 * as a claim rather than as the thing being denied.
 */

export const USAGE_DISCLOSURE_TITLE = "What we measure";

/**
 * The intro, then what one count holds — one entry per stored kind of value,
 * in the order the `usage_events` columns are written (`api/test/usage.test.ts`
 * maps every column to exactly one of these).
 */
export const USAGE_RECORD_INTRO =
  "We count which screens you open, which buttons you use, and which steps you finish (for example signing up, setting up, the tour, picking a team, accepting a coach invite, whether a payment went through), so we can see where people get stuck and whether they come back. Each count holds:";

export const USAGE_RECORD_FIELDS = [
  "the screen or button, from a fixed list of names",
  "whether you were using the app as a player or a coach",
  "whether you were on a phone, a tablet or a desktop",
  "the time",
  "a random code for your account",
];

/** The sentence about the server totals, true for every role including admin. */
export const USAGE_TOTALS_SENTENCE =
  "The server also keeps simple totals of a few actions with no code at all, so they cannot be switched off for one person.";

/** What an admin account is told in place of the switch (D229). */
export const USAGE_ADMIN_SENTENCE = "Admin accounts are not counted.";

/**
 * The paragraphs every reader sees, in reading order, below the list above.
 * "Aggregate is not the same as anonymous" stays (`usageDisclosure.test.js`).
 */
export const USAGE_DISCLOSURE_PARAGRAPHS = [
  "The random code is there instead of anything that names you. A count holds no name, no email address, no user or session ID, no IP address, no location, and nothing you typed: no goal, note, reflection or check-in. The report we read never shows the code.",
  "The counting happens in the app and on our own servers. Nothing is sent to an analytics company, and there are no tracking cookies.",
  "We keep the detailed counts for 90 days. After that we keep only totals, with no code attached. Deleting your account deletes your detailed counts.",
  "Aggregate is not the same as anonymous at our size. While we test the app with a small group, the two people who run CricketCompass can see exact counts, so either of them could recognise someone by their pattern of use. Nobody else sees them.",
];

/** The last paragraph for a player or a coach: the switch, then the totals. */
export const USAGE_SWITCH_PARAGRAPH =
  "You can switch this off below at any time. Switching off stops the counting for your account and deletes its detailed counts. " +
  USAGE_TOTALS_SENTENCE;

/** The last paragraph for an admin: no switch, because nothing is counted. */
export const USAGE_ADMIN_PARAGRAPH = `${USAGE_ADMIN_SENTENCE} ${USAGE_TOTALS_SENTENCE}`;

/** The switch row's own words (`UsageDisclosureCard`). */
export const USAGE_SWITCH_TITLE = "Count my use of the app";
export const USAGE_SWITCH_DESC = "Switching off deletes your detailed counts.";
export const USAGE_SWITCH_ERROR = "We couldn't change this. Nothing has changed.";
/** Not in the issue: said when the saved choice could not be read, so no switch is drawn. */
export const USAGE_SWITCH_LOAD_ERROR = "We couldn't load this setting. Try again later.";

/**
 * The short version, used on the signed-out door (the sign-up form): short
 * enough to be read in a form, complete enough to stand on its own for
 * someone who never opens Settings.
 */
export const USAGE_DISCLOSURE_SHORT =
  "We count which screens and buttons get used and which steps you finish, with a random code for your account instead of your name, to learn which features matter. Never anything you type. It stays on our servers, the detail is kept 90 days, and you can switch it off in Settings.";

/**
 * D446 (#1626): the new look opens "What we measure" with these three lines
 * and folds the full text above behind a "Read more" button. Nothing above is
 * changed or removed; the full text is still rendered, word for word, once the
 * button is pressed.
 */
export const USAGE_SUMMARY_LINES = [
  "We count which screens and buttons get used and which steps you finish, to learn what to improve. Never anything you type.",
  "Only the two people who run CricketCompass see the counts, and at our size they could recognise your pattern of use.",
  "You can switch it off below at any time. That also deletes your detailed counts.",
];
export const USAGE_READ_MORE = "Read more";
export const USAGE_READ_LESS = "Show less";

/** D446 (#1626): the new-look sign-up's one line, followed by a link to /privacy. */
export const USAGE_DISCLOSURE_ONE_LINE = "We count which features get used, never what you type. You can switch it off in Settings.";
